
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  Alert
} from '@mui/material'
import { fmtDate, getWorkoutTitle, unixTimestampsFromStartTime, mapPunchesToTargets, computeStats } from './util'
import { usePaginatedCollection, useUser } from '@/pb'
import { useMemo, useState } from 'react'

export default function WorkoutHistory({ workoutTemplates, onOpenWorkout }) {
  const { user } = useUser()
  const [workoutHistoryPage, setWorkoutHistoryPage] = useState(1)
  const {
    records: workoutHistory,
    totalPages,
    loaded: workoutHistoryLoaded,
    refetch
  } = usePaginatedCollection('music_workouts', {
    page: workoutHistoryPage,
    perPage: 30,
    sort: '-created',
    filter: `userId = "${user.id}"`
  })

  const workoutHistoryExtended = useMemo(() => {
    if (!workoutHistoryLoaded || !workoutTemplates) return [];

    return workoutHistory.map((workout) => {
      const template = workoutTemplates.find((w) => w.id === workout.templateId);

      if (!workout.punches || !template) {
        return {
          ...workout,
          workoutTitle: 'Unknown',
          targets: [],
          punches: [],
          targetsMap: {},
          stats: null,
          averageDelay: null,
          efficiency: null,
        };
      }

      const workoutTitle = getWorkoutTitle(template);
      const targets = template.punchTargets;
      const punchTimestamps = workout.punches.map(punch => punch.timestamp)
      const punchForces = workout.punches.map(punch => punch.force);
      const punchesFull = workout.punches.map(punch => ({
        ...punch,
        timestamp: punch.timestamp - workout.startTime
      }));

      const avgForce = (punchForces.reduce((a, b) => a + b, 0) / punchForces.length).toFixed(0);

      const targetsMap = mapPunchesToTargets(punchTimestamps, targets);
      const stats = computeStats(targetsMap, targets, punchTimestamps);

      return {
        ...workout,
        template,
        workoutTitle,
        targets,
        punchTimestamps,
        punchForces,
        punchesFull,
        targetsMap,
        stats,
        avgForce,
        averageDelay: stats ? stats.avgDelay.toFixed(2) : null,
        efficiency: stats ? stats.efficiency.toFixed(0) : null,
      };
    });
  }, [workoutHistory, workoutTemplates, workoutHistoryLoaded]);


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
        Архив тренировок
      </Typography>

      <List disablePadding>
        {workoutHistoryExtended.map((workout) => (
          <Paper key={workout.id} variant="outlined" sx={{ mb: 1.25, borderRadius: 3 }}>
            <ListItemButton onClick={() => onOpenWorkout(workout.id)}>
              <ListItemText
                primary={workout.workoutTitle}
                secondary={`${fmtDate(workout.created)} · Эффективность: ${workout.efficiency}% · Сила: ${workout.avgForce} · Задержка: ${workout.averageDelay}с`}
              />
            </ListItemButton>
          </Paper>
        ))}
      </List>

      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          disabled={workoutHistoryPage <= 1 || !workoutHistoryLoaded}
          onClick={() => setWorkoutHistoryPage(1)}
        >
          В начало
        </Button>
        <Button
          variant="outlined"
          disabled={workoutHistoryPage <= 1 || !workoutHistoryLoaded}
          onClick={() => setWorkoutHistoryPage(p => p - 1)}
        >
          Назад
        </Button>
        <Typography variant="body2">
          Страница {workoutHistoryPage} из {totalPages}
        </Typography>
        <Button
          variant="outlined"
          disabled={workoutHistoryPage >= totalPages || !workoutHistoryLoaded}
          onClick={() => setWorkoutHistoryPage(p => p + 1)}
        >
          Вперёд
        </Button>

        <Button
          variant="outlined"
          disabled={workoutHistoryPage >= totalPages || !workoutHistoryLoaded}
          onClick={() => setWorkoutHistoryPage(totalPages)}
        >
          В конец
        </Button>
      </Box>
    </Box>
  )
}